import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  FiFilm, FiImage, FiLoader, FiMic, FiMusic, FiPlus, FiSend, FiTrash2, FiUser, FiVideo,
} from 'react-icons/fi';
import {
  cCategories, cCreatePost, cMyChannels, cMyGroups, cUpdatePost, cUpload, cUploadAudio,
  cUploadVideo, cUsers, fullUrl,
} from '../../api';
import { useCommunity } from '../../contexts/CommunityContext';
import { useLanguage } from '../../i18n';
import { audioError, formatBytes, formatTime } from '../../utils/audio';
import { isVertical, ratioFromSize, videoError } from '../../utils/media';
import { parseVideo } from '../../utils/video';
import AudioPlayer from './AudioPlayer';
import Linkify from './Linkify';
import PostOptionsPanel from './PostOptionsPanel';
import UploadedVideo from './UploadedVideo';
import UserAvatar from './UserAvatar';
import { BLUE, BLUE_GRADIENT } from './brand';

const emptyBlock = () => ({ images: [], video_url: '', description: '' });

/**
 * Background presets for a text-only **Status** post (Facebook style).
 * The API only accepts `linear-gradient(…deg, #hex 0%, #hex 100%)`, `#hex` or
 * `rgb()/rgba()`, so every preset here is safe to store.
 */
export const STATUS_BACKGROUNDS = [
  { value: '' },
  { value: 'linear-gradient(135deg, #0B5FDB 0%, #083E93 100%)' },
  { value: 'linear-gradient(135deg, #FB6E08 0%, #B45309 100%)' },
  { value: 'linear-gradient(135deg, #0ea5e9 0%, #1d4ed8 100%)' },
  { value: 'linear-gradient(135deg, #10b981 0%, #065f46 100%)' },
  { value: 'linear-gradient(135deg, #8b5cf6 0%, #4c1d95 100%)' },
  { value: 'linear-gradient(135deg, #ec4899 0%, #9d174d 100%)' },
  { value: 'linear-gradient(135deg, #f59e0b 0%, #b45309 100%)' },
  { value: 'linear-gradient(135deg, #111827 0%, #374151 100%)' },
  { value: 'linear-gradient(135deg, #0B5FDB 0%, #FB6E08 100%)' },
  { value: '#0B5FDB' },
  { value: '#FB6E08' },
  { value: '#10b981' },
  { value: '#111827' },
];

/**
 * "Write an article" box — brand logo, Category Post dropdown, title,
 * description (links + **@mentions** in blue), images / GIF upload, a video link
 * with a live ratio-aware preview and an **MP3 music upload** (≤ 10 MB, auto
 * compressed) whose cover image spins 360° while it plays.
 *
 * Long articles: the **+ Add more** button appends another section (each with
 * its own images + video link + description). Also used to edit (`initial`).
 */
export default function PostComposer({ initial = null, onSaved, onCancel, startOpen = false,
                                      focusKey = '', asChannel = null }) {
  const { t, lang } = useLanguage();
  const { user, openAuth, actingChannel } = useCommunity();
  const [open, setOpen] = useState(!!initial || startOpen);
  const rootRef = useRef(null);
  const [cats, setCats] = useState([]);
  const [title, setTitle] = useState(initial?.title || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [categoryId, setCategoryId] = useState(initial?.category_id || initial?.category?.id || '');
  const [images, setImages] = useState(initial?.images || []);
  const [videoUrl, setVideoUrl] = useState(initial?.video_url || '');
  const [blocks, setBlocks] = useState(initial?.blocks || []);
  const [audio, setAudio] = useState(initial?.audio
    ? { url: initial.audio, title: initial.audio_title || '', duration: initial.audio_duration || 0 }
    : null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadTo, setUploadTo] = useState('main');   // 'main' or a block index
  // ── “ជម្រើសនៃការបង្ហោះសារ”: who sees it, comments, sharing, where it goes ──
  const [visibility, setVisibility] = useState(initial?.visibility || 'public');
  const [allowComments, setAllowComments] = useState(initial ? initial.allow_comments !== false : true);
  const [allowShare, setAllowShare] = useState(initial ? initial.allow_share !== false : true);
  const [externalShare, setExternalShare] = useState(
    initial ? initial.external_share !== false : true,
  );
  const [channelId, setChannelId] = useState(initial?.channel_id || null);
  const [groupId, setGroupId] = useState(initial?.group_id || null);
  const [channels, setChannels] = useState([]);   // my channels (max 3)
  const [groups, setGroups] = useState([]);       // the groups I am in
  // Publishing “as a channel”: either the one I switched to with the account
  // switcher, or the channel whose page I am standing on (asChannel prop).
  const postingChannel = asChannel || actingChannel;
  const [audioBusy, setAudioBusy] = useState(false);
  const [mentions, setMentions] = useState({ open: false, items: [], token: '', start: -1 });
  const [postType, setPostType] = useState(initial?.post_type || 'status');
  const [video, setVideo] = useState(initial?.video
    ? {
      url: initial.video,
      thumb: initial.video_thumb || '',
      duration: initial.video_duration || 0,
      width: initial.video_width || 0,
      height: initial.video_height || 0,
    }
    : null);
  const [videoBusy, setVideoBusy] = useState(false);
  const [bg, setBg] = useState(initial?.bg || '');        // status text background
  const fileRef = useRef(null);
  const blockFileRef = useRef(null);
  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const descriptionRef = useRef(null);

  const loadCats = () => {
    if (cats.length) return;
    cCategories().then((rows) => setCats(Array.isArray(rows) ? rows : [])).catch(() => {});
  };

  // My channels (max 3) + the groups I belong to — the “publish in…” dropdowns
  // of the post-options panel. Loaded once, when the composer is opened.
  useEffect(() => {
    if (!open || !user) return undefined;
    let alive = true;
    Promise.all([cMyChannels().catch(() => null), cMyGroups().catch(() => null)])
      .then(([ch, gr]) => {
        if (!alive) return;
        setChannels(ch?.items || []);
        setGroups(gr?.items || []);
      });
    return () => { alive = false; };
  }, [open, user]);

  const linkVideo = parseVideo(videoUrl);      // the pasted YouTube/FB/… link

  /** Look for "@word" right before the caret and suggest members to mention. */
  const refreshMentions = (value, caret) => {
    const upToCaret = String(value).slice(0, caret);
    const match = upToCaret.match(/@([A-Za-z0-9._-]{0,30})$/);
    if (!match) { setMentions({ open: false, items: [], token: '', start: -1 }); return; }
    const token = match[1];
    const start = caret - token.length - 1;
    cUsers({ q: token || undefined, limit: 6 })
      .then((res) => {
        const items = (res?.items || []).filter(
          (u) => !user || u.id !== user.id,
        );
        setMentions({ open: items.length > 0, items, token, start });
      })
      .catch(() => setMentions({ open: false, items: [], token: '', start: -1 }));
  };

  const insertMention = (username) => {
    const el = descriptionRef.current;
    const caret = el && typeof el.selectionStart === 'number' ? el.selectionStart : description.length;
    const before = description.slice(0, mentions.start >= 0 ? mentions.start : caret);
    const after = description.slice(caret);
    const next = `${before}@${username} ${after}`;
    setDescription(next);
    setMentions({ open: false, items: [], token: '', start: -1 });
    setTimeout(() => {
      if (!el) return;
      const pos = before.length + username.length + 2;
      el.focus();
      el.setSelectionRange(pos, pos);
    }, 0);
  };

  const expand = () => {
    if (!user) { openAuth('signin', t('loginRequiredDesc')); return; }
    loadCats();
    setOpen(true);
    scrollIntoView();
  };

  /** Bring the writing box into the middle of the screen (smooth). */
  const scrollIntoView = () => {
    const el = rootRef.current;
    if (!el) return;
    try {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (e) { /* very old browsers */ }
    const field = el.querySelector('input:not([type="file"]), textarea');
    if (field) setTimeout(() => { try { field.focus({ preventScroll: true }); } catch (e) { field.focus(); } }, 420);
  };

  // The ＋ button / "Write" link opens the composer → scroll straight to it.
  useEffect(() => {
    if (!startOpen) return undefined;
    setOpen(true);
    const tm = setTimeout(scrollIntoView, 80);
    return () => clearTimeout(tm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startOpen, focusKey]);

  const putUploaded = (urls, target) => {
    if (target === 'main') {
      setImages((prev) => [...prev, ...urls]);
      return;
    }
    setBlocks((prev) => prev.map((b, i) => (i === target ? { ...b, images: [...(b.images || []), ...urls] } : b)));
  };

  const pickImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const target = uploadTo;
    setUploading(true);
    try {
      const res = await cUpload(files);
      putUploaded(res.urls || [], target);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
      if (blockFileRef.current) blockFileRef.current.value = '';
    }
  };

  /** Upload an MP3 (≤ 10 MB) — the backend compresses it and keeps quality. */
  const pickAudio = async (e) => {
    const file = e.target.files?.[0];
    if (audioRef.current) audioRef.current.value = '';
    if (!file) return;
    const bad = audioError(file);
    if (bad) { toast.error(bad); return; }
    setAudioBusy(true);
    try {
      const res = await cUploadAudio(file, 'music');
      setAudio({
        url: res.url,
        title: (file.name || '').replace(/\.[^.]+$/, ''),
        duration: res.duration || 0,
      });
      toast.success(`${t('audioUploaded')} · ${formatBytes(res.original_size)} → ${formatBytes(res.size)}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    } finally {
      setAudioBusy(false);
    }
  };

  const removeAudio = () => setAudio(null);

  /** Upload a video (≤ 10 MB) — the backend compresses it and makes a poster. */
  const pickVideo = async (e) => {
    const file = e.target.files?.[0];
    if (videoRef.current) videoRef.current.value = '';
    if (!file) return;
    const bad = videoError(file);
    if (bad) { toast.error(bad); return; }
    setVideoBusy(true);
    try {
      const res = await cUploadVideo(file);
      setVideo({
        url: res.url,
        thumb: res.thumb || '',
        duration: res.duration || 0,
        width: res.width || 0,
        height: res.height || 0,
      });
      setPostType(res.vertical ? 'reel' : 'video');
      toast.success(`${t('videoUploaded')} · ${formatBytes(res.original_size)} → ${formatBytes(res.size)}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    } finally {
      setVideoBusy(false);
    }
  };

  const removeVideo = () => setVideo(null);

  const addBlock = () => setBlocks((prev) => [...prev, emptyBlock()]);
  const patchBlock = (index, patch) =>
    setBlocks((prev) => prev.map((b, i) => (i === index ? { ...b, ...patch } : b)));
  const removeBlock = (index) => setBlocks((prev) => prev.filter((_, i) => i !== index));

  const reset = () => {
    setTitle('');
    setDescription('');
    setCategoryId('');
    setImages([]);
    setVideoUrl('');
    setBlocks([]);
    setAudio(null);
    setVideo(null);
    setPostType('status');
    setBg('');
    // back to the default post options
    setVisibility('public');
    setAllowComments(true);
    setAllowShare(true);
    setExternalShare(true);
    setChannelId(null);
    setGroupId(null);
  };

  const cleanBlocks = () => blocks
    .map((b) => ({
      images: (b.images || []).filter(Boolean),
      video_url: (b.video_url || '').trim(),
      description: (b.description || '').trim(),
    }))
    .filter((b) => b.images.length || b.video_url || b.description);

  const submit = async (e) => {
    e.preventDefault();
    const nextBlocks = cleanBlocks();
    if (!title.trim() && !description.trim() && !images.length && !videoUrl.trim()
        && !nextBlocks.length && !audio && !video) {
      toast.error(t('writeSomething'));
      return;
    }
    setBusy(true);
    const payload = {
      title: title.trim(),
      description,
      images,
      video_url: videoUrl.trim(),
      blocks: nextBlocks,
      category_id: categoryId ? Number(categoryId) : null,
      audio: audio?.url || '',
      audio_title: audio?.title || '',
      audio_duration: audio?.duration || 0,
      post_type: video ? postType : (postType === 'video' || postType === 'reel' ? 'status' : postType),
      bg: postType === 'status' ? (bg || '') : '',
      video: video?.url || '',
      video_thumb: video?.thumb || '',
      video_duration: video?.duration || 0,
      video_width: video?.width || 0,
      video_height: video?.height || 0,
      // the “ជម្រើសនៃការបង្ហោះសារ” panel
      visibility,
      allow_comments: allowComments,
      allow_share: allowShare,
      external_share: externalShare,
      // posted as the channel → the article belongs to the channel account
      channel_id: postingChannel ? postingChannel.id : (channelId || null),
      group_id: groupId || null,
    };
    try {
      const saved = initial
        ? await cUpdatePost(initial.id, payload)
        : await cCreatePost(payload);
      toast.success(initial ? t('updatedOk') : t('posted'));
      if (!initial) reset();
      setOpen(false);
      if (onSaved) onSaved(saved);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    } finally {
      setBusy(false);
    }
  };


  const blockEditor = (b, i) => (
    <div key={`blk-${i}`} className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-600 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-extrabold uppercase" style={{ color: BLUE }}>
          {t('section')} {i + 1}
        </span>
        <button
          type="button"
          onClick={() => removeBlock(i)}
          className="inline-flex items-center gap-1 text-xs text-red-500 hover:underline"
        >
          <FiTrash2 className="w-3.5 h-3.5" /> {t('remove')}
        </button>
      </div>

      <textarea
        value={b.description || ''}
        onChange={(e) => patchBlock(i, { description: e.target.value })}
        rows={3}
        placeholder={t('postDescription')}
        className="w-full rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-900 px-3 py-2 text-sm outline-none focus:border-gray-400"
      />

      <input
        value={b.video_url || ''}
        onChange={(e) => patchBlock(i, { video_url: e.target.value })}
        placeholder={`${t('videoLink')} — 16:9 or 9:16`}
        className="w-full rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-900 px-3 py-2 text-sm outline-none focus:border-gray-400"
      />
      {b.video_url?.trim() && (
        <p className="text-[11px] text-gray-500">
          {(() => { const v = parseVideo(b.video_url); return `${v.type} · ${v.ratioKey}`; })()}
        </p>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => { setUploadTo(i); if (blockFileRef.current) blockFileRef.current.click(); }}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 text-xs font-semibold text-gray-600 dark:text-gray-300"
        >
          {uploading && uploadTo === i ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiImage className="w-3.5 h-3.5" />}
          {t('addImages')}
        </button>
        {(b.images || []).length > 0 && (
          <span className="inline-flex gap-1.5 flex-wrap">
            {(b.images || []).map((src, k) => (
              <span key={src + k} className="relative w-14 h-14 rounded-xl overflow-hidden border border-gray-100 dark:border-gray-800">
                <img src={fullUrl(src)} alt={`section ${i + 1} ${k + 1}`} className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => patchBlock(i, { images: (b.images || []).filter((x) => x !== src) })}
                  className="absolute top-0.5 right-0.5 p-0.5 rounded bg-black/55 text-white"
                  aria-label={t('remove')}
                >
                  <FiTrash2 className="w-3 h-3" />
                </button>
              </span>
            ))}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div ref={rootRef} style={{ scrollMarginTop: 76 }} className="anim-fade-up bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-4">
      <button type="button" onClick={expand} className="w-full flex items-center gap-3 text-left">
        <span className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: BLUE_GRADIENT }}>
          <img src={`${process.env.PUBLIC_URL}/ckl.png`} alt="CodeKhmerLearning" className="w-7 h-7" />
        </span>
        <UserAvatar user={user} size={40} />
        <span className="flex-1 min-w-0 text-sm text-gray-500 dark:text-gray-400 truncate">
          {user ? (open ? t('writePost') : t('writeSomething')) : t('joinCommunity')}
        </span>
        <span className="hidden sm:inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-white text-sm font-bold" style={{ background: BLUE }}>
          <FiSend className="w-4 h-4" /> {t('writePost')}
        </span>
      </button>

      {/* Shared hidden file inputs: one for the main images, one for the sections */}
      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
        multiple
        hidden
        onChange={pickImages}
      />
      <input
        ref={blockFileRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
        multiple
        hidden
        onChange={pickImages}
      />

      {open && (
        <form onSubmit={submit} className="mt-4 space-y-3">
          {/* កំពុងបង្ហោះជាឆានែល — the article belongs to the channel account */}
          {postingChannel && (
            <p className="flex items-center gap-2 rounded-xl bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs font-bold text-amber-800 dark:text-amber-200">
              {postingChannel.avatar ? (
                <img src={fullUrl(postingChannel.avatar)} alt={postingChannel.name} className="w-6 h-6 rounded-lg object-cover" />
              ) : (
                <span className="w-6 h-6 rounded-lg flex items-center justify-center bg-amber-500 text-white text-[11px] font-extrabold">
                  {(postingChannel.name || 'C').slice(0, 1).toUpperCase()}
                </span>
              )}
              {t('postingAs').replace('{name}', postingChannel.name)}
            </p>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-bold text-gray-500">{t('categoryLabel')}:</span>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-900 px-2.5 py-1.5 text-sm"
            >
              <option value="">{t('allCategories')}</option>
              {cats.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.icon} {lang === 'kh' ? (c.name_km || c.name) : c.name}
                </option>
              ))}
            </select>
          </div>

          {/* Facebook-like post type: Status · Photo · Video (Reel) */}
          <div className="flex items-center gap-1.5 flex-wrap rounded-2xl border border-gray-200 dark:border-gray-700 p-1">
            {[
              { key: 'status', icon: <FiUser className="w-4 h-4" />, label: t('postTypeStatus') },
              { key: 'photo', icon: <FiImage className="w-4 h-4" />, label: t('postTypePhoto') },
              { key: 'video', icon: <FiFilm className="w-4 h-4" />, label: t('postTypeVideo') },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                data-sfx="toggle"
                onClick={() => setPostType(tab.key)}
                className={`flex-1 min-w-[92px] inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs sm:text-sm font-bold transition ${
                  postType === tab.key
                    ? 'text-white'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
                style={postType === tab.key ? { background: BLUE } : undefined}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-gray-400 -mt-1">
            {postType === 'status' && t('postTypeStatusHint')}
            {postType === 'photo' && t('postTypePhotoHint')}
            {postType === 'video' && t('postTypeVideoHint')}
          </p>

          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('postTitle')}
            className="w-full rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-900 px-3.5 py-2.5 font-semibold focus:border-gray-400 outline-none"
          />

          <div className="relative">
            <textarea
              ref={descriptionRef}
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                refreshMentions(e.target.value, e.target.selectionStart ?? e.target.value.length);
              }}
              onKeyUp={(e) => refreshMentions(description, e.currentTarget.selectionStart ?? 0)}
              onBlur={() => setTimeout(() => setMentions((m) => ({ ...m, open: false })), 150)}
              rows={5}
              placeholder={`${t('postDescription')}\n${t('postDescriptionHint')}\n${t('mentionHint')}`}
              className={`w-full rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-900 px-3.5 py-2.5 text-sm focus:border-gray-400 outline-none ${
                postType === 'status' && bg ? 'text-white font-bold placeholder-white/70 text-center' : ''
              }`}
              style={postType === 'status' && bg ? { background: bg, borderColor: 'transparent' } : undefined}
            />
            <p className="mt-1 text-[11px] text-gray-400 inline-flex items-center gap-1.5">
              <FiUser className="w-3 h-3" /> {t('mentionTip')}
            </p>

            {/* @mention suggestions */}
            {mentions.open && (
              <div className="absolute z-40 mt-1 w-full max-w-sm rounded-xl bg-white dark:bg-gray-900 shadow-2xl border border-gray-100 dark:border-gray-800 p-1.5 anim-pop">
                {mentions.items.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); insertMention(u.username); }}
                    className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    <UserAvatar user={u} size={28} />
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold truncate">{u.name || u.username}</span>
                      <span className="block text-[11px] text-gray-400">@{u.username}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {description.trim() && (
            <div className="rounded-xl bg-gray-50 dark:bg-gray-800/60 p-3">
              <p className="text-[11px] font-bold text-gray-400 uppercase mb-1">Preview</p>
              <Linkify text={description} className="text-sm text-gray-800 dark:text-gray-100" />
            </div>
          )}


          {postType === 'status' && (
            <div className="rounded-2xl border border-gray-200 dark:border-gray-700 p-3 space-y-2">
              <p className="text-xs font-bold text-gray-500 inline-flex items-center gap-1.5">
                <FiImage className="w-4 h-4" /> {t('statusBackground')}
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                {STATUS_BACKGROUNDS.map((b) => (
                  <button
                    key={b.value || 'none'}
                    type="button"
                    data-sfx="tap"
                    onClick={() => setBg(b.value)}
                    title={b.value || t('bgNone')}
                    className={`w-9 h-9 rounded-full border-2 flex items-center justify-center text-[10px] font-bold transition ${
                      bg === b.value ? 'border-gray-900 dark:border-white scale-110' : 'border-transparent'
                    }`}
                    style={b.value ? { background: b.value, color: '#fff' } : undefined}
                  >
                    {!b.value && (bg === '' ? '✓' : '—')}
                  </button>
                ))}
              </div>
              {bg && description.trim() && (
                <div className="rounded-xl px-4 py-6 text-center" style={{ background: bg }}>
                  <p className="text-white font-extrabold text-base leading-snug whitespace-pre-wrap break-words">
                    {description.trim().slice(0, 200)}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Images + GIF — shown for a “Photo” post */}
          {postType === 'photo' && (
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => { setUploadTo('main'); if (fileRef.current) fileRef.current.click(); }}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:border-gray-400"
              >
                {uploading && uploadTo === 'main' ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiImage className="w-4 h-4" />}
                {uploading && uploadTo === 'main' ? t('uploading') : t('addImages')}
              </button>
              <span className="text-[11px] text-gray-400">PNG · JPG · GIF · WEBP</span>
            </div>
            {images.length > 0 && (
              <div className="mt-2 grid grid-cols-3 sm:grid-cols-4 gap-2">
                {images.map((src, i) => (
                  <div key={src + i} className="relative rounded-xl overflow-hidden border border-gray-100 dark:border-gray-800" style={{ aspectRatio: '1 / 1' }}>
                    <img src={fullUrl(src)} alt={`upload ${i + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setImages((prev) => prev.filter((x) => x !== src))}
                      className="absolute top-1 right-1 p-1 rounded-lg bg-black/55 text-white"
                      aria-label={t('remove')}
                    >
                      <FiTrash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          )}

          {/* Video: upload (≤ 10 MB, auto compressed) or a link — Video / Reel post */}
          {postType === 'video' && (
          <div className="space-y-3">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => videoRef.current && videoRef.current.click()}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:border-gray-400"
                >
                  {videoBusy ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiFilm className="w-4 h-4" />}
                  {videoBusy ? t('compressing') : t('addVideo')}
                </button>
                <span className="text-[11px] text-gray-400">{t('videoHint')}</span>
              </div>
              <input
                ref={videoRef}
                type="file"
                accept="video/mp4,video/quicktime,video/x-m4v,video/webm,video/x-matroska,.mp4,.mov,.m4v,.webm,.mkv"
                hidden
                onChange={pickVideo}
              />
            </div>

            {video && (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-bold text-gray-500">
                    {t('videoFile')} · {formatTime(video.duration)}
                    {isVertical(video.width, video.height) ? ` · ${t('reelBadge')} 9:16` : ` · ${ratioFromSize(video.width, video.height).replace(' / ', ':')}`}
                  </span>
                  <button
                    type="button"
                    onClick={removeVideo}
                    className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-gray-800"
                    aria-label={t('remove')}
                  >
                    <FiTrash2 className="w-4 h-4" />
                  </button>
                </div>
                <UploadedVideo post={{ video: video.url, video_thumb: video.thumb, video_duration: video.duration, video_width: video.width, video_height: video.height, is_reel: isVertical(video.width, video.height) }} inline />
                {isVertical(video.width, video.height) && (
                  <p className="text-[11px]" style={{ color: BLUE }}>🎬 {t('reelVerticalHint')}</p>
                )}
              </div>
            )}

            <div>
              <label className="flex items-center gap-2 text-xs font-bold text-gray-500 mb-1.5">
                <FiVideo className="w-4 h-4" /> {t('videoLink')}
              </label>
              <input
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=… · /shorts/… · facebook.com/reel/…"
                className="w-full rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-900 px-3.5 py-2.5 text-sm focus:border-gray-400 outline-none"
              />
              {videoUrl.trim() && (
                <div className="mt-2 flex items-center gap-3 rounded-xl border border-gray-100 dark:border-gray-800 p-2">
                  {linkVideo.thumb ? (
                    <img src={linkVideo.thumb} alt="preview" className="w-24 object-cover rounded-lg" style={{ aspectRatio: linkVideo.ratio }} />
                  ) : (
                    <span className="w-24 rounded-lg bg-gradient-to-br from-[#0B5FDB] to-[#083E93]" style={{ aspectRatio: linkVideo.ratio }} />
                  )}
                  <span className="text-xs text-gray-500">
                    {linkVideo.type} · {linkVideo.ratioKey}
                  </span>
                </div>
              )}
            </div>
          </div>
          )}

          {/* MP3 music / voice (≤ 10 MB) — compressed automatically, cover spins */}
          <div>
            <label className="flex items-center gap-2 text-xs font-bold text-gray-500 mb-1.5">
              <FiMusic className="w-4 h-4" /> {t('audioFile')} — {t('audioHint')}
            </label>

            <input
              ref={audioRef}
              type="file"
              accept="audio/mpeg,audio/mp3,audio/mp4,audio/m4a,audio/aac,audio/ogg,audio/opus,audio/webm,audio/wav,.mp3,.m4a,.wav,.ogg,.webm"
              hidden
              onChange={pickAudio}
            />

            {!audio ? (
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => audioRef.current && audioRef.current.click()}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:border-gray-400"
                >
                  {audioBusy ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiMusic className="w-4 h-4" />}
                  {audioBusy ? t('compressing') : t('audioFile')}
                </button>
                <span className="text-[11px] text-gray-400">MP3 · M4A · WAV · OGG · WEBM</span>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    value={audio.title || ''}
                    onChange={(e) => setAudio((a) => ({ ...a, title: e.target.value }))}
                    placeholder={t('audioTitle')}
                    className="flex-1 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-900 px-3 py-2 text-sm outline-none focus:border-gray-400"
                  />
                  <button
                    type="button"
                    onClick={removeAudio}
                    className="p-2 rounded-lg text-red-500 hover:bg-red-50 dark:hover:bg-gray-800"
                    aria-label={t('remove')}
                  >
                    <FiTrash2 className="w-4 h-4" />
                  </button>
                </div>
                <AudioPlayer
                  src={audio.url}
                  title={audio.title}
                  cover={images[0] || ''}
                  round
                  size={104}
                />
                <p className="text-[11px] text-gray-400">
                  <FiMic className="w-3 h-3 inline" /> {t('coverImage')} — {formatTime(audio.duration)}
                </p>
              </div>
            )}
          </div>

          {/* Long article: extra sections (+ Add more) */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-gray-500">{t('sectionsHint')}</span>
              {blocks.length > 0 && (
                <span className="text-[11px] text-gray-400">{blocks.length} × {t('section')}</span>
              )}
            </div>
            {blocks.map(blockEditor)}
            <button
              type="button"
              onClick={addBlock}
              className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold border-2"
              style={{ borderColor: BLUE, color: BLUE }}
            >
              <FiPlus className="w-4 h-4" /> {blocks.length ? t('addSection') : t('addMore')}
            </button>
          </div>

          {/* “ជម្រើសនៃការបង្ហោះសារ” — Picture / Video / Music buttons + the
              privacy, comment, sharing and “publish in…” options */}
          <PostOptionsPanel
            busy={busy || uploading || videoBusy}
            lockedChannel={postingChannel}
            visibility={visibility} setVisibility={setVisibility}
            allowComments={allowComments} setAllowComments={setAllowComments}
            allowShare={allowShare} setAllowShare={setAllowShare}
            externalShare={externalShare} setExternalShare={setExternalShare}
            channelId={channelId} setChannelId={setChannelId} channels={channels}
            groupId={groupId} setGroupId={setGroupId} groups={groups}
            onPicture={() => { setPostType('photo'); if (fileRef.current) fileRef.current.click(); }}
            onVideo={() => { setPostType('video'); setTimeout(() => videoRef.current && videoRef.current.click(), 60); }}
            onMusic={() => { if (audioRef.current) audioRef.current.click(); }}
          />

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={() => { setOpen(false); if (onCancel) onCancel(); }}
              className="px-4 py-2 rounded-full text-sm font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              {initial ? t('cancel') : t('close')}
            </button>
            <button
              type="submit"
              data-sfx="success"
              disabled={busy || uploading}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-white text-sm font-bold disabled:opacity-60"
              style={{ background: BLUE }}
            >
              {busy ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiSend className="w-4 h-4" />}
              {initial ? t('update') : t('publish')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

